import { EProjectPermission } from "@frontend-shared/types";
import { useMemo } from "react";

import { useCallAssistantPermissions } from "@/hooks/use-call-assistant-permissions";
import { usePermissionCheck } from "@/hooks/use-permission";
import { useSkillPermissions } from "@/hooks/use-skill-permissions";
import type { IGlobalSearchPermissionInputs } from "@/lib/global-search/types";

// NOTE: Module-level so usePermissionCheck's memo sees a stable array.
const PERMISSIONS: EProjectPermission[] = [
  EProjectPermission.PRIVATE_ASSISTANT_READ,
  EProjectPermission.PUBLIC_ASSISTANT_READ,
  EProjectPermission.KNOWLEDGE_READ,
  EProjectPermission.MODEL_READ,
  EProjectPermission.PROMPT_LIBRARY_READ,
  EProjectPermission.ALL_MEMBERS_ANALYTICS_READ,
  EProjectPermission.ANALYTICS_READ,
  EProjectPermission.PROBE_READ,
  EProjectPermission.AUTOMATION_READ,
  EProjectPermission.MARKETPLACE_AUTOMATION_READ,
  EProjectPermission.DOC_RECOGNITION_READ,
  EProjectPermission.INTEGRATION_READ,
  EProjectPermission.CUSTOM_INTEGRATION_READ,
  EProjectPermission.CUSTOM_INTEGRATION_WRITE,
  EProjectPermission.CUSTOM_INTEGRATION_DELETE,
];

// NOTE: NSAI-6589 — the ACTIVE project's permissions the static index gates
// read, from the same hooks routes/index.tsx uses.
export const useGlobalSearchPermissionInputs =
  (): IGlobalSearchPermissionInputs => {
    const [
      hasPrivateAssistantRead,
      hasPublicAssistantRead,
      hasKnowledgeRead,
      hasModelRead,
      hasPromptLibraryRead,
      hasAllMembersAnalyticsRead,
      hasAnalyticsRead,
      hasProbeRead,
      hasAutomationRead,
      hasMarketplaceAutomationRead,
      hasDocRecognitionRead,
      hasIntegrationRead,
      hasCustomIntegrationRead,
      hasCustomIntegrationWrite,
      hasCustomIntegrationDelete,
    ] = usePermissionCheck(PERMISSIONS);
    const { canRead: hasCallAssistantRead } = useCallAssistantPermissions();
    const { canReadAny: hasAnySkillRead, canReadOrgCatalog } =
      useSkillPermissions();

    return useMemo(
      () => ({
        hasPrivateAssistantRead,
        hasPublicAssistantRead,
        hasKnowledgeRead,
        hasModelRead,
        hasPromptLibraryRead,
        hasAllMembersAnalyticsRead,
        hasAnalyticsRead,
        hasProbeRead,
        hasAutomationRead,
        hasMarketplaceAutomationRead,
        hasDocRecognitionRead,
        hasIntegrationRead,
        hasCustomIntegrationRead,
        hasCustomIntegrationWrite,
        hasCustomIntegrationDelete,
        hasCallAssistantRead,
        hasAnySkillRead,
        hasSkillCatalogRead: canReadOrgCatalog,
      }),
      [
        hasPrivateAssistantRead,
        hasPublicAssistantRead,
        hasKnowledgeRead,
        hasModelRead,
        hasPromptLibraryRead,
        hasAllMembersAnalyticsRead,
        hasAnalyticsRead,
        hasProbeRead,
        hasAutomationRead,
        hasMarketplaceAutomationRead,
        hasDocRecognitionRead,
        hasIntegrationRead,
        hasCustomIntegrationRead,
        hasCustomIntegrationWrite,
        hasCustomIntegrationDelete,
        hasCallAssistantRead,
        hasAnySkillRead,
        canReadOrgCatalog,
      ],
    );
  };
